import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import Link from "next/link";
import Slider from "react-slick";
import { getStoryblokApi } from "@storyblok/react";
import ArticlePreviewBox from "../../src/articles/ArticlePreviewBox";
import {
  IconChevronRight,
  IconPlayVideo,
  IconRightArrow,
  IconLeftArrow,
} from "../../styles/icons";
import ArticleOfTheDay from "../../src/articles/ArticleOfTheDay";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dynamic from "next/dynamic";
import Head from "next/head";

const ArticleSearchInput = dynamic(
  () => import("../../src/articles/ArticleSearchInput"),
);

function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <IconButton
      size="sm"
      variant="carousel"
      aria-label="Next"
      icon={<IconRightArrow />}
      onClick={onClick}
      zIndex="5"
    />
  );
}

function SamplePrevArrow(props) {
  const { onClick } = props;
  return (
    <IconButton
      size="sm"
      variant="carousel"
      aria-label="Previous"
      icon={<IconLeftArrow />}
      onClick={onClick}
      z-index="100"
    />
  );
}

const fallbackArticle = {
  name: "10 Ways to Shift to a More Hopeful Outlook",
  created_at: "2025-01-12T18:27:24.778Z",
  published_at: "2025-01-12T18:27:24.885Z",
  updated_at: "2025-01-12T18:27:24.902Z",
  id: 607605134,
  uuid: "1e11f36e-ddec-43e8-a6b9-7561d0b634d4",
  content: {
    _uid: "906b40bc-97cc-4482-a228-7c10d029c7f8",
    body: {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [
            {
              text: "Our brains are wired to focus on threats, a remnant of our evolutionary past when immediate danger was a constant concern. This tendency-not helped by our news outlets focusing on bad news-can lead us to catastrophise about the future, creating a picture of impending disaster that may never materialise.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Scientists have shown that the structure of our brains can be impacted by our thought patterns. Neuroscience shows us that we can rewire our brains through conscious effort and practice. In short, being more positive is good for us.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Here are a few tips for harnessing the power of your mind and create a future you're excited about.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "1. Help is good",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Many of us feel the need to tackle our problems alone. This solitary approach, however, goes against our nature as social beings. Research has consistently shown that social support is crucial for mental health and well-being.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Don't hesitate to ask for help when you need it. If the idea of a face-to-face conversation feels daunting, consider a side-by-side activity. Many people find it easier to open up while engaged in a shared task or sport. Whether it's going for a walk with a friend or working on a project together, these activities can provide a less intense setting for meaningful conversations.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "2. Awe is wonderful",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Experiencing awe can have a profound impact on our psychological well-being. Just like love, wonder is all around. Look for it. This could be as simple as watching a sunset, visiting a museum, or attending a live performance. The collective energy of shared experiences can be particularly powerful. By exposing ourselves to awe-inspiring moments, we can shift our perspective and feel more connected to the world around us.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "3. Fun isn't just for kids",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "As adults, especially in high-pressure careers, we often forget the importance of unstructured, joyful activities.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: 'This is what comedian and creativity expert John Cleese calls the "open mode" of thinking. In his ',
              type: "text",
            },
            {
              text: "lecture on creativity",
              type: "text",
              marks: [
                {
                  type: "link",
                  attrs: {
                    href: "https://www.youtube.com/watch?v=Pb5oIIPO62g",
                    title: null,
                  },
                },
              ],
            },
            {
              text: ', Cleese contrasts this with the "closed mode" ─ our default state of stress and seriousness.',
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "By engaging in play, we naturally shift into this open mode, allowing for greater problem-solving abilities and innovative thinking. This isn't just about having fun ─ it's about creating a mental environment conducive to breakthroughs and novel solutions, both in our personal and professional lives.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Make time for play in your life. This could mean getting down on the ground with your children or grandchildren, taking up a new hobby, or engaging in sports you enjoy. The key is to find activities that fully engage you and bring a sense of freedom and joy.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "4. Now is better than later",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: 'Procrastination often stems from fear or perfectionism. However, waiting for the "perfect" moment can lead to missed opportunities. Remember, done is better than perfect. Start that project you\'ve been putting off, have that difficult conversation, or take that leap of faith. As the saying goes, "The best time to plant a tree was 20 years ago. The second-best time is now."',
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "5. Work on your courage",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Courage isn't the absence of fear, but rather the ability to act despite it. Like a muscle, courage grows stronger with use.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Start small. Set yourself a challenge each day that pushes you slightly out of your comfort zone. It could be as simple as speaking up in a meeting or trying a new food. Over time, these small acts of courage will build your confidence to tackle bigger challenges, building self-esteem and a greater sense of satisfaction.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "6. Who you are, not what you have",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "In our achievement-oriented society, it's easy to define ourselves by our possessions or titles. However, true fulfilment comes from who we are, not what we have. And that's not just some soppy thread running through a Hallmark movie, there is research to support it.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: 'Focus on developing your character and values rather than accumulating accolades. Ask yourself, "What kind of person do I want to be?" rather than "What do I want to have?". This shift in focus can lead to more meaningful goals and a stronger sense of purpose.',
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "This doesn't mean abandoning ambition, but rather ensuring that your ambitions serve your deeper purpose.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "7. Never stop learning",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Adopting a growth mindset-the belief that abilities can be developed through dedication and hard work-can significantly impact your approach to learning and challenges; it makes it easier to put more effort into learning.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "And learning can improve both our health and wellbeing, especially as we age. Neuroscience research has shown that learning new skills can increase the density of white matter in the brain, improving its efficiency and adaptability while potentially staving off conditions like dementia.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "There are any number of challenges you can set yourself. A common challenge is to learn a new language, but you could create a culinary bucket list of dishes you want to master making, or learn a professional skill you currently delegate to someone else or have always been interested in.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "The key to keeping your mind flexible and open to new possibilities is challenging yourself regularly.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "8. Shake it up",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Routine can be comforting, but it can lead to stagnation. By introducing variety into our lives, we can enhance our cognitive flexibility and creativity.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "To experience fresh perspectives and new ideas, try taking a different route to work, rearranging your office, or spending a day doing things with your non-dominant hand.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Additionally, consider more significant changes. Travel to new places, immerse yourself in different cultures, or take on a project outside your usual area of expertise. These experiences can broaden your horizons and challenge your assumptions, leading to personal growth and new insights.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "9. Be more active",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "The mind-body connection is powerful. Regular physical activity not only improves our physical health but also enhances cognitive function and emotional wellbeing. Regular exercise in middle age can reduce risks of dementia in later life.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Find physical activities you enjoy, whether it's sailing, running, or playing a sport. The key is consistency rather than intensity. Even a daily walk can bring benefits, for both your physical and mental health.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Moreover, physical activity can be a powerful tool for stress management. Exercise reduces levels of stress hormones like cortisol and adrenaline while stimulating the production of endorphins, which are natural mood elevators.",
              type: "text",
            },
          ],
        },
        {
          type: "heading",
          attrs: {
            level: 4,
          },
          content: [
            {
              text: "10. Power of free will",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "The debate over free will has raged for centuries, but recent research suggests that simply believing in free will can have positive effects on our behaviour and well-being.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Believing in free will doesn't mean denying the influence of external factors. Rather, it's about recognising that within the constraints of our circumstances, we have the power to make choices that shape our lives and the world around us.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "We need to make choices, take action, and accept responsibility which, in turn, helps us make better choices. If it feels difficult, then start small. Decide to do something different, like to go to the cinema one afternoon, take action, and accept any consequences that come your way.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "By actively engaging with life, taking risks, and believing in your ability to shape your future, you can transform uncertainty from a source of anxiety into a wellspring of possibility.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "Stephen Bruyant-Langer is the author of ",
              type: "text",
              marks: [
                {
                  type: "bold",
                },
              ],
            },
            {
              text: "The Existential Playbook - How to Survive, Live and Thrive",
              type: "text",
              marks: [
                {
                  type: "italic",
                },
                {
                  type: "bold",
                },
              ],
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "References",
              type: "text",
              marks: [
                {
                  type: "bold",
                },
              ],
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[1]: Doidge, N. 2007 . The Brain That Changes Itself. Penguin Books.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[2]: Rueger, S. Y., Malecki, C. K., Pyun, Y., Aycock, C., & Coyle, S. 2016 . A meta-analytic review of the association between perceived social support and depression in childhood and adolescence. Psychological Bulletin, 142 10 , 1017-1067.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[3] ",
              type: "text",
            },
            {
              text: "https://www.youtube.com/watch?v=Pb5oIIPO62g",
              type: "text",
              marks: [
                {
                  type: "link",
                  attrs: {
                    href: "https://www.youtube.com/watch?v=Pb5oIIPO62g",
                    title: null,
                  },
                },
              ],
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[4]: Ryan, R. M., & Deci, E. L. 2000 . Self-determination theory and the facilitation of intrinsic motivation, social development, and well-being. American Psychologist, 55 1 , 68-78.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[5]: Dweck, C. S. 2006 . Mindset: The new psychology of success. Random House.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[6]: Scholz, J., Klein, M. C., Behrens, T. E., & Johansen-Berg, H. 2009 . Training induces changes in white-matter architecture. Nature Neuroscience, 12 11 , 1370-1371.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[7]: Andel, R., Crowe, M., Pedersen, N. L., Fratiglioni, L., Johansson, B., & Gatz, M. 2008 . Physical Exercise at Midlife and Risk of Dementia Three Decades Later: A Population-Based Study of Swedish Twins. The Journals of Gerontology Series A: Biological Sciences and Medical Sciences, 63 1 , 62-66.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[8]: Jackson, E. M. 2013 . Stress relief: The role of exercise in stress management. ACSM's Health & Fitness Journal, 17 3 , 14-19.",
              type: "text",
            },
          ],
        },
        {
          type: "paragraph",
          content: [
            {
              text: "[9]: Vohs, K. D., & Schooler, J. W. 2008 . The Value of Believing in Free Will: Encouraging a Belief in Determinism Increases Cheating. Psychological Science, 19 1 , 49-54.",
              type: "text",
            },
          ],
        },
      ],
    },
    title: "10 Ways to Shift to a More Hopeful Outlook",
    authors: [
      {
        name: "Anonymous",
        created_at: "2025-01-10T16:16:15.486Z",
        published_at: "2025-01-10T16:17:46.392Z",
        updated_at: "2025-01-10T16:17:46.411Z",
        id: 607424309,
        uuid: "5ae1e229-15c9-44f2-88a2-12e45a407145",
        content: {
          _uid: "4153b7d3-5503-4a80-9f8d-7afd9f743a86",
          image: {
            id: null,
            alt: null,
            name: "",
            focus: null,
            title: null,
            source: null,
            filename:
              "https://upload.wikimedia.org/wikipedia/commons/4/48/Easel_with_empty_canvas_%283033798968%29.jpg",
            copyright: null,
            fieldtype: "asset",
            meta_data: {},
          },
          lastName: "Author",
          legacyId: "",
          subtitle: "",
          component: "author",
          firstName: "Anonymous",
          internalId: "",
          description: {
            type: "doc",
            content: [
              {
                type: "paragraph",
              },
            ],
          },
          middleNames: "",
          socialLinks: "[]",
          overrideSidebarVideoId: "",
        },
        slug: "anonymous",
        full_slug: "authors/anonymous",
        sort_by_date: null,
        position: -5060,
        tag_list: [],
        is_startpage: false,
        parent_id: 502966668,
        meta_data: null,
        group_id: "e6cf44f2-ee67-4f7c-9bf8-124c4cf8635f",
        first_published_at: "2025-01-10T16:17:46.392Z",
        release_id: null,
        lang: "default",
        path: null,
        alternates: [],
        default_full_slug: null,
        translated_slugs: null,
        _stopResolving: true,
      },
    ],
    summary: null,
    metatags: {
      title: "10 Ways to Shift to a More Hopeful Outlook",
      plugin: "seo_metatags",
      og_image: "",
      og_title: "10 Ways to Shift to a More Hopeful Outlook",
      description: "",
      twitter_image: "",
      twitter_title: "10 Ways to Shift to a More Hopeful Outlook",
      og_description: "",
      twitter_description: "",
    },
    component: "article",
    headerImage: {
      alt: "Default image",
      name: "",
      focus: "",
      title: "",
      source: "",
      filename:
        "https://legacy.welldoing.org/storage/app/uploads/public/5ff/5bd/ba1/5ff5bdba1ebde923488519.jpg",
      copyright: "",
      fieldtype: "asset",
      meta_data: {
        alt: "",
        title: "",
        source: "",
        copyright: "",
      },
      is_external_url: true,
    },
    readFurther: null,
    legacy_published_at: "2025-01-23T11:30:00+00:00",
  },
  slug: "10-ways-shift-more-hopeful-outlook",
  full_slug: "article/10-ways-shift-more-hopeful-outlook",
  sort_by_date: null,
  position: -32720,
  tag_list: ["articleoftheday", "Mind", "mindset", "change"],
  is_startpage: false,
  parent_id: 607601446,
  meta_data: null,
  group_id: "025f4b53-5440-4288-9efd-9c7d49cd69de",
  first_published_at: "2025-01-12T18:27:24.885Z",
  release_id: null,
  lang: "default",
  path: null,
  alternates: [],
  default_full_slug: null,
  translated_slugs: null,
};

// Fetch content from prismic
export async function getStaticProps({ params }) {
  const storyblokApi = getStoryblokApi();
  try {
    var tagData = await storyblokApi.getAll(`cdn/tags`, {
      starts_with: "article",
    });
    tagData.sort((a, b) => b.taggings_count - a.taggings_count);
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  let articles = [];
  let trending = [];
  let articleOfTheDay;
  try {
    const {
      data: { stories },
    } = await storyblokApi.get(`cdn/stories`, {
      starts_with: "article",
      per_page: 5,
      resolve_relations: ["article.authors"],
      sort_by: "content.legacy_published_at:desc",
    });
    articles = stories;
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
  try {
    const {
      data: { stories },
    } = await storyblokApi.get(`cdn/stories`, {
      resolve_relations: ["article.authors"],
      with_tag: "trending",
    });
    trending = stories;
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  try {
    const {
      data: { stories },
    } = await storyblokApi.get(`cdn/stories`, {
      resolve_relations: ["article.authors"],
      with_tag: "articleoftheday",
    });
    articleOfTheDay = stories.length > 0 ? stories[0] : fallbackArticle;
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  const articleTagMap = {};
  try {
    const datasourceEntries = await storyblokApi.getAll(
      `cdn/datasource_entries`,
      {
        datasource: "articletagmap",
      },
    );
    datasourceEntries.forEach((tag) => {
      articleTagMap[tag.name] = tag.value;
    });
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  const articleGroups = [
    {
      heading: "Latest Posts",
      buttonText: "View all latest",
      articles: articles,
      slug: "article/latest",
    },
    {
      heading: "Trending Posts of the Month",
      buttonText: "View all trending",
      articles: trending,
      slug: "article/trending",
    },
  ];

  const cleanedTags = tagData.map((tag) => {
    if (articleTagMap[tag.name]) {
      return { ...tag, slug: tag.name, name: articleTagMap[tag.name] };
    } else {
      return { ...tag, slug: tag.name, name: tag.name };
    }
  });

  return {
    props: {
      articleGroups,
      articleOfTheDay,
      navigationTags: cleanedTags,
    },
    revalidate: 3600,
  };
}

const TagLozenge = ({ tag, onClickSeeAll = null }) => {
  if (tag.uid === "video") {
    return (
      <Link href={`/article/listBy?tag=${tag.uid}`} prefetch={false}>
        <Button
          variant="solid"
          bgGradient="linear-gradient(76.64deg, #C84A03 19.53%, rgba(245, 131, 26, 0.56) 143.39%);"
          pl="8px"
          display={{ base: "none", xl: "flex" }}
        >
          <IconPlayVideo color="white" size="32px" />
          <Text textStyle="button1">{tag.name}</Text>
        </Button>
      </Link>
    );
  } else if (tag.name === "see-all-tags") {
    return (
      <Button onClick={onClickSeeAll} variant="solid" minW="unset">
        <Text textStyle="button1">See all tags</Text>
      </Button>
    );
  } else {
    return (
      <Link href={`/article/tags/${tag.slug}`} prefetch={false}>
        <Button
          variant="outline"
          _hover={{ bgColor: "brand.400", color: "white" }}
          display="flex"
          minW="unset"
        >
          <Text textStyle="button1">{tag.name}</Text>
        </Button>
      </Link>
    );
  }
};

const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  initialSlide: 0,
  appendDots: (dots) => (
    <div
      style={{
        position: "relative",
        bottom: 0,
        paddingTop: "10px",
        paddingBottom: "10px",
      }}
    >
      <ul style={{ margin: "0px" }}> {dots} </ul>
    </div>
  ),
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        dots: true,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        initialSlide: 2,
        dots: true,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true,
      },
    },
  ],
};

const CarouselRow = ({ articles }) => {
  const sliderRef = useRef();
  return (
    <Box flex="1" height="100%" overflow="hidden">
      <Slider {...sliderSettings} ref={sliderRef}>
        {articles.slice(0, 4).map((article, articleIndex) => {
          return (
            <Stack
              display="flex !important"
              width="100%"
              direction="row"
              justifyContent="center"
            >
              <ArticlePreviewBox
                image={article.content.headerImage.filename}
                imageAlt={article.content.headerImage?.alt}
                title={article.content.title}
                author={article.content.authors[0].name}
                slug={article.slug}
                key={articleIndex}
                width="95%"
              />
            </Stack>
          );
        })}
      </Slider>
    </Box>
  );
};

export default function ArticleIndex({
  articleGroups,
  articleOfTheDay,
  navigationTags,
}) {
  const [seeAllTags, setSeeAllTags] = useState(false);
  const [screenSizeLarge, setScreenSizeLarge] = useState(false);
  const matches = useMediaQuery("(min-width: 1536px)");

  useEffect(() => {
    setScreenSizeLarge(matches);
  }, [matches]);

  return (
    <Container
      maxW={{ base: "100%", xl: "80%" }}
      boxSizing="border-box"
      overflowX="hidden"
    >
      <Head>
        <title>Welldoing.org - Articles</title>
        <link rel="canonical" href="https://welldoing.org/article" />
      </Head>
      <Box width="100%" display="flex">
        <Box mx="auto" my="50px" width="100%">
          <Flex align="center" gap="10px">
            <Link href="/" prefetch={false}>
              <Text color="brand.400" cursor="pointer">{`Homepage`}</Text>
            </Link>
            <IconChevronRight size="22px" />
            <Text>{`Welldoing Articles`}</Text>
          </Flex>

          <Box my="50px">
            <Grid
              templateColumns={{ base: "1fr", xl: "2fr 1fr" }}
              templateRows={{ base: "1fr", xl: "repeat(2, 1fr)" }}
              mb="30px"
              rowGap="15px"
            >
              <GridItem>
                <Text as="h1" textStyle="h1">
                  Welldoing Articles
                </Text>
              </GridItem>
              <GridItem
                gridRow={{ base: 3, xl: 1 }}
                gridColumn={{ base: 1, xl: 2 }}
                rowSpan={{ base: 1, xl: 2 }}
                placeSelf={{ base: "normal", xl: "center" }}
                w="100%"
              >
                <ArticleSearchInput />
              </GridItem>
              <GridItem
                gridRow={{ base: 2, xl: 2 }}
                gridColumn={{ base: 1, xl: 1 }}
              >
                <Text textStyle="body1" color="brand.200">
                  Explore our community features, case studies, opinion pieces,
                  and more
                </Text>
              </GridItem>
            </Grid>

            <Flex flexDirection={"column"} mb="40px" gap="10px">
              <Text textStyle="h7" fontWeight="bold">
                Filter articles by:
              </Text>
              <Flex
                flexWrap={{ base: "wrap", xl: "nowrap" }}
                overflowX={{ xl: "scroll" }}
              >
                {navigationTags.map((tag, index) => {
                  if (!seeAllTags && index > 7) {
                    return;
                  }
                  return <TagLozenge tag={tag} index={index} key={index} />;
                })}
                {!seeAllTags && (
                  <TagLozenge
                    tag={{ name: "see-all-tags" }}
                    onClickSeeAll={() => setSeeAllTags(true)}
                  />
                )}
              </Flex>
            </Flex>
            <ArticleOfTheDay
              image={articleOfTheDay.content.headerImage.filename}
              title={articleOfTheDay.name}
              author={articleOfTheDay.content.authors[0].name}
              slug={articleOfTheDay.slug}
              authorImage={
                articleOfTheDay.content.authors[0].content.image.filename
              }
              publishedAt={articleOfTheDay.published_at}
            />
            {Object.entries(articleGroups).map(
              ([groupUid, group], articlegroupIndex) => {
                if (group.articles.length < 1) {
                  return;
                }
                return (
                  <Box
                    mb="94px"
                    display={{ base: "none", md: "block" }}
                    key={articlegroupIndex}
                  >
                    <Flex justify="space-between" align="center" mb="30px">
                      <Text textStyle="h3">{group.heading}</Text>
                      <Link href={group.slug} prefetch={false}>
                        <Button>
                          <Text textStyle="button1">{group.buttonText}</Text>
                        </Button>
                      </Link>
                    </Flex>
                    <Flex direction="row" justify="flex-start" gap="20px">
                      {group.articles
                        .slice(0, screenSizeLarge ? 5 : 4)
                        .map((article, articleIndex) => {
                          return (
                            <ArticlePreviewBox
                              image={article.content.headerImage.filename}
                              imageAlt={article.content.headerImage?.alt}
                              title={article.content.title}
                              author={article.content.authors[0].name}
                              slug={article.slug}
                              key={articleIndex}
                            />
                          );
                        })}
                    </Flex>
                  </Box>
                );
              },
            )}
            {Object.entries(articleGroups).map(
              ([groupUid, group], articlegroupIndex) => {
                if (group.articles.length < 1) {
                  return;
                }
                return (
                  <Box
                    mb="94px"
                    display={{ base: "block", md: "none" }}
                    key={articlegroupIndex}
                  >
                    <Flex justify="space-between" align="center" mb="30px">
                      <Text textStyle="h3">{group.heading}</Text>
                      <Link href={group.slug} prefetch={false}>
                        <Button>
                          <Text textStyle="button1">{group.buttonText}</Text>
                        </Button>
                      </Link>
                    </Flex>
                    <Flex direction="row" justify="center" gap="20px">
                      <CarouselRow articles={group.articles} />
                    </Flex>
                  </Box>
                );
              },
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
